import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useSearchParams,
} from "react-router-dom";

import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Table,
  Spinner,
  Badge,
  Pagination,
} from "react-bootstrap";

import {
  Download,
  RefreshCcw,
  Search,
  ChevronUp,
  ChevronDown,
} from "react-feather";

import {
  fetchFinancialYearsThunk,
} from "../../../store/slices/financialYearSlice";

import {
  fetchWaterDcbThunk,
  exportWaterDcbExcelThunk,
  exportWaterDcbPdfThunk,
  clearWaterDcb,
} from "../../../store/slices/waterDcbSlice";

import "./ReportPage.css";

// ============================================================
// FINANCIAL YEAR HELPER
// Example:
// 2025-26 -> 2025
// ============================================================

function fyToStartYear(fyStr) {
  const value = String(
    fyStr || "",
  ).trim();

  const match =
    value.match(/\d{4}/);

  return match
    ? match[0]
    : "";
}

// ============================================================
// ADD BLANK ROWS BETWEEN HOUSES
// ============================================================

function buildRowsWithBlanks(
  rows = [],
) {
  const output = [];

  for (
    let index = 0;
    index < rows.length;
    index += 1
  ) {
    const row =
      rows[index];

    output.push(row);

    if (
      row.__blankAfterHouse
    ) {
      output.push({
        __blank: true,

        __key:
          `b1-${row.home_id}-${index}`,
      });

      output.push({
        __blank: true,

        __key:
          `b2-${row.home_id}-${index}`,
      });
    }
  }

  return output;
}

// ============================================================
// COMPONENT
// ============================================================

const WaterDcbReportPage = () => {
  const dispatch =
    useDispatch();

  const [searchParams] =
    useSearchParams();

  // ==========================================================
  // HOME ID FROM URL
  // ==========================================================

  const homeIdParam =
    String(
      searchParams.get(
        "home_id",
      ) || "",
    ).trim();

  const home_id =
    homeIdParam
      ? Number(
          homeIdParam,
        )
      : 0;

  // ==========================================================
  // REDUX
  // ==========================================================

  const fyList =
    useSelector(
      (state) =>
        state.financialYears
          ?.list || [],
    );

  const fyLoading =
    useSelector(
      (state) =>
        state.financialYears
          ?.loading || false,
    );

  const {
    rows = [],
    loading = false,
    error = null,
    meta = {},

    excelLoading = false,
    excelError = null,

    pdfLoading = false,
    pdfError = null,
  } = useSelector(
    (state) =>
      state.waterDcb || {},
  );

  // ==========================================================
  // STATE
  // ==========================================================

  const [
    fyYear,
    setFyYear,
  ] = useState("");

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    sortField,
    setSortField,
  ] = useState(
    "home_id",
  );

  const [
    sortDirection,
    setSortDirection,
  ] = useState(
    "desc",
  );

  const [
    page,
    setPage,
  ] = useState(1);

  const [
    pageSize,
    setPageSize,
  ] = useState(20);

  // ==========================================================
  // LOAD FINANCIAL YEARS
  // ==========================================================

  useEffect(() => {
    dispatch(
      fetchFinancialYearsThunk(),
    );
  }, [dispatch]);

  // ==========================================================
  // SET CURRENT FY
  // ==========================================================

  useEffect(() => {
    if (
      !fyList.length ||
      fyYear
    ) {
      return;
    }

    const current =
      fyList.find(
        (item) =>
          Number(
            item?.current,
          ) === 1,
      );

    const startYear =
      fyToStartYear(
        current
          ?.financial_year,
      );

    if (startYear) {
      setFyYear(
        startYear,
      );
    }
  }, [
    fyList,
    fyYear,
  ]);

  // ==========================================================
  // TABLE REQUEST FILTERS
  // ==========================================================

  const getRequestFilters = (
    requestPage,
    requestPageSize,
  ) => ({
    fy_year:
      fyYear,

    home_id:
      home_id || "",

    page:
      requestPage,

    pageSize:
      requestPageSize,

    sort_field:
      sortField,

    sort_direction:
      sortDirection,
  });

  // ==========================================================
  // EXPORT FILTERS
  // ==========================================================

  const getExportFilters =
    () => ({
      fy_year:
        fyYear,

      home_id:
        home_id || "",

      sort_field:
        sortField,

      sort_direction:
        sortDirection,
    });

  // ==========================================================
  // LOAD
  // ==========================================================

  const handleLoad =
    () => {
      if (
        !fyYear ||
        !pageSize
      ) {
        return;
      }

      setPage(1);

      dispatch(
        fetchWaterDcbThunk(
          getRequestFilters(
            1,
            pageSize,
          ),
        ),
      );
    };

  // ==========================================================
  // AUTO LOAD
  // ==========================================================

  useEffect(() => {
    if (
      !fyYear ||
      !pageSize
    ) {
      return;
    }

    dispatch(
      fetchWaterDcbThunk(
        getRequestFilters(
          page,
          pageSize,
        ),
      ),
    );
  }, [
    dispatch,
    fyYear,
    home_id,
    page,
    pageSize,
    sortField,
    sortDirection,
  ]);

  // ==========================================================
  // CLEAR
  // ==========================================================

  const handleClear =
    () => {
      setFyYear("");

      setSearch("");

      setSortField(
        "home_id",
      );

      setSortDirection(
        "desc",
      );

      setPage(1);

      setPageSize(20);

      dispatch(
        clearWaterDcb(),
      );

      dispatch(
        fetchFinancialYearsThunk(),
      );
    };

  // ==========================================================
  // SORT
  // ==========================================================

  const handleSort =
    (field) => {
      setPage(1);

      if (
        sortField === field
      ) {
        setSortDirection(
          (
            currentDirection,
          ) =>
            currentDirection ===
            "asc"
              ? "desc"
              : "asc",
        );

        return;
      }

      setSortField(
        field,
      );

      setSortDirection(
        "asc",
      );
    };

  // ==========================================================
  // SORT ICON
  // ==========================================================

  const renderSortIcon =
    (field) => {
      if (
        sortField !== field
      ) {
        return (
          <span className="ms-1 report-sort-default-icon">
            <ChevronUp
              size={11}
            />

            <ChevronDown
              size={11}
            />
          </span>
        );
      }

      return sortDirection ===
        "asc" ? (
        <ChevronUp
          size={14}
          className="ms-1"
        />
      ) : (
        <ChevronDown
          size={14}
          className="ms-1"
        />
      );
    };

  // ==========================================================
  // SEARCH CURRENT PAGE
  // ==========================================================

  const filteredRows =
    useMemo(() => {
      const query =
        search
          .trim()
          .toLowerCase();

      const baseRows =
        !query
          ? [...rows]
          : rows.filter(
              (row) => {
                const searchableText = `
                  ${row.family_head || ""}
                  ${row.qr_code || ""}
                  ${row.door_no || ""}
                  ${row.date || ""}
                  ${row.bill_no || ""}
                  ${row.rct_no || ""}
                  ${row.mode || ""}
                  ${row.opening_balance ?? ""}
                  ${row.bill_amount ?? ""}
                  ${row.rct_amount ?? ""}
                  ${row.discount_amount ?? ""}
                  ${row.discount_remarks || ""}
                  ${row.closing_balance ?? ""}
                `
                  .toLowerCase()
                  .replace(
                    /\s+/g,
                    " ",
                  );

                return searchableText.includes(
                  query,
                );
              },
            );

      const rowsWithBlankFlags =
        baseRows.map(
          (
            row,
            index,
          ) => {
            const next =
              baseRows[
                index + 1
              ];

            const isLastOfHouse =
              !next ||
              Number(
                next.home_id,
              ) !==
                Number(
                  row.home_id,
                );

            return {
              ...row,

              __blankAfterHouse:
                isLastOfHouse,
            };
          },
        );

      return buildRowsWithBlanks(
        rowsWithBlankFlags,
      );
    }, [
      rows,
      search,
    ]);

  // ==========================================================
  // DOWNLOAD BLOB HELPER
  // ==========================================================

  const downloadBlob = (
    fileData,
    mimeType,
    fileName,
  ) => {
    const blob =
      fileData instanceof Blob
        ? fileData
        : new Blob(
            [fileData],
            {
              type:
                mimeType,
            },
          );

    const url =
      window.URL.createObjectURL(
        blob,
      );

    const link =
      document.createElement(
        "a",
      );

    link.href = url;

    link.download =
      fileName;

    document.body.appendChild(
      link,
    );

    link.click();

    link.remove();

    window.URL.revokeObjectURL(
      url,
    );
  };

  // ==========================================================
  // EXCEL EXPORT
  //
  // NEW API:
  // GET /api/reports/water-dcb/export?format=excel
  // ==========================================================

  const exportExcel =
    async () => {
      if (
        !fyYear ||
        excelLoading ||
        pdfLoading
      ) {
        return;
      }

      try {
        const excelBlob =
          await dispatch(
            exportWaterDcbExcelThunk(
              getExportFilters(),
            ),
          ).unwrap();

        const fileName =
          home_id
            ? `water_dcb_home_${home_id}_fy_${fyYear}.xlsx`
            : `water_dcb_fy_${fyYear}.xlsx`;

        downloadBlob(
          excelBlob,

          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",

          fileName,
        );
      } catch (
        exportError
      ) {
        console.error(
          "Excel export failed:",
          exportError,
        );
      }
    };

  // ==========================================================
  // PDF EXPORT
  //
  // NEW API:
  // GET /api/reports/water-dcb/export?format=pdf
  // ==========================================================

  const exportPDF =
    async () => {
      if (
        !fyYear ||
        pdfLoading ||
        excelLoading
      ) {
        return;
      }

      try {
        const pdfBlob =
          await dispatch(
            exportWaterDcbPdfThunk(
              getExportFilters(),
            ),
          ).unwrap();

        const fileName =
          home_id
            ? `water_dcb_home_${home_id}_fy_${fyYear}.pdf`
            : `water_dcb_fy_${fyYear}.pdf`;

        downloadBlob(
          pdfBlob,

          "application/pdf",

          fileName,
        );
      } catch (
        exportError
      ) {
        console.error(
          "PDF export failed:",
          exportError,
        );
      }
    };

  // ==========================================================
  // PAGINATION META
  // ==========================================================

  const totalPages =
    Math.max(
      1,
      Number(
        meta?.totalPages ||
          1,
      ),
    );

  const currentPage =
    Math.max(
      1,
      Number(
        meta?.page ||
          page,
      ),
    );

  const totalRecords =
    Number(
      meta?.totalRecords ||
        0,
    );

  // ==========================================================
  // PAGINATION
  // ==========================================================

  const renderPagination =
    () => {
      if (
        totalPages <= 1
      ) {
        return null;
      }

      const items = [];

      const maxButtons =
        5;

      let start =
        Math.max(
          1,

          currentPage -
            Math.floor(
              maxButtons /
                2,
            ),
        );

      let end =
        Math.min(
          totalPages,

          start +
            maxButtons -
            1,
        );

      start =
        Math.max(
          1,

          end -
            maxButtons +
            1,
        );

      items.push(
        <Pagination.Prev
          key="prev"
          disabled={
            currentPage ===
              1 ||
            loading
          }
          onClick={() =>
            setPage(
              (
                previous,
              ) =>
                Math.max(
                  1,
                  previous -
                    1,
                ),
            )
          }
        />,
      );

      for (
        let pageNumber =
          start;
        pageNumber <=
        end;
        pageNumber += 1
      ) {
        items.push(
          <Pagination.Item
            key={
              pageNumber
            }
            active={
              pageNumber ===
              currentPage
            }
            onClick={() =>
              setPage(
                pageNumber,
              )
            }
            disabled={
              loading
            }
          >
            {
              pageNumber
            }
          </Pagination.Item>,
        );
      }

      items.push(
        <Pagination.Next
          key="next"
          disabled={
            currentPage ===
              totalPages ||
            loading
          }
          onClick={() =>
            setPage(
              (
                previous,
              ) =>
                Math.min(
                  totalPages,
                  previous +
                    1,
                ),
            )
          }
        />,
      );

      return (
        <Pagination className="mb-0">
          {items}
        </Pagination>
      );
    };

  // ==========================================================
  // SORTABLE HEADER
  // ==========================================================

  const SortableHeader =
    ({
      field,
      children,
    }) => (
      <th>
        <button
          type="button"
          className={`report-sort-btn ${
            sortField ===
            field
              ? "is-active"
              : ""
          }`}
          onClick={() =>
            handleSort(
              field,
            )
          }
        >
          {children}

          {renderSortIcon(
            field,
          )}
        </button>
      </th>
    );

  // ==========================================================
  // JSX
  // ==========================================================

  return (
    <div className="report-page">
      <Card className="report-card">

        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <Card.Header className="report-card-header">

          <div className="report-header-row">

            <div>

              <div className="report-title">

                Water DCB - Report

                {home_id ? (
                  <Badge
                    bg="info"
                    className="ms-2"
                  >
                    House ID:{" "}
                    {home_id}
                  </Badge>
                ) : null}

              </div>

              <div className="report-subtitle">

                {home_id
                  ? "Single House Passbook (home_id filter)"
                  : "Financial Year wise Water DCB for all water houses"}

              </div>

            </div>

          </div>

        </Card.Header>

        <Card.Body>

          {/* ================================================= */}
          {/* ERRORS */}
          {/* ================================================= */}

          {error ? (
            <div className="alert alert-danger mb-3">
              {error}
            </div>
          ) : null}

          {excelError ? (
            <div className="alert alert-danger mb-3">
              {excelError}
            </div>
          ) : null}

          {pdfError ? (
            <div className="alert alert-danger mb-3">
              {pdfError}
            </div>
          ) : null}

          {/* ================================================= */}
          {/* FILTER */}
          {/* ================================================= */}

          <div className="report-filter-box">

            <Row className="g-3">

              <Col
                lg={
                  home_id
                    ? 6
                    : 4
                }
                md={6}
              >

                <Form.Label className="report-label">
                  Financial Year
                </Form.Label>

                <Form.Select
                  value={
                    fyYear
                  }
                  onChange={(
                    event,
                  ) => {
                    setFyYear(
                      event
                        .target
                        .value,
                    );

                    setPage(1);
                  }}
                  disabled={
                    fyLoading ||
                    loading ||
                    excelLoading ||
                    pdfLoading
                  }
                >

                  <option value="">
                    Select FY
                  </option>

                  {fyList.map(
                    (
                      item,
                    ) => {
                      const startYear =
                        fyToStartYear(
                          item
                            ?.financial_year,
                        );

                      if (
                        !startYear
                      ) {
                        return null;
                      }

                      return (
                        <option
                          key={String(
                            item.id,
                          )}
                          value={
                            startYear
                          }
                        >
                          {
                            item.financial_year
                          }

                          {" "}

                          {Number(
                            item.current,
                          ) === 1
                            ? "(Current)"
                            : ""}
                        </option>
                      );
                    },
                  )}

                </Form.Select>

              </Col>

              {/* ============================================= */}
              {/* RECORDS PER PAGE */}
              {/* ============================================= */}

              {!home_id ? (

                <Col
                  lg={4}
                  md={6}
                >

                  <Form.Label className="report-label">
                    Records Per Page
                  </Form.Label>

                  <Form.Control
                    type="number"
                    min={1}
                    value={
                      pageSize ||
                      ""
                    }
                    onChange={(
                      event,
                    ) => {
                      const value =
                        event
                          .target
                          .value;

                      setPage(1);

                      setPageSize(
                        value
                          ? Number(
                              value,
                            )
                          : "",
                      );
                    }}
                    disabled={
                      loading ||
                      excelLoading ||
                      pdfLoading
                    }
                  />

                  <div
                    className="text-muted mt-1"
                    style={{
                      fontSize: 12,
                    }}
                  >
                    Enter how many house records you want per page
                  </div>

                </Col>

              ) : (

                <Col
                  lg={6}
                  md={6}
                >

                  <Form.Label className="report-label">
                    Mode
                  </Form.Label>

                  <Form.Control
                    value="Single House (home_id)"
                    disabled
                  />

                  <div
                    className="text-muted mt-1"
                    style={{
                      fontSize: 12,
                    }}
                  >
                    This page is opened from House Profile → View DCB
                  </div>

                </Col>

              )}

              {/* ============================================= */}
              {/* FILTER ACTIONS */}
              {/* ============================================= */}

              <Col
                lg={4}
                className="d-flex align-items-end"
              >

                <div className="report-filter-actions">

                  <Button
                    className="report-btn-primary"
                    variant="primary"
                    onClick={
                      handleLoad
                    }
                    disabled={
                      loading ||
                      excelLoading ||
                      pdfLoading ||
                      !fyYear ||
                      !pageSize
                    }
                  >

                    {loading ? (
                      <>
                        <Spinner
                          size="sm"
                          className="me-2"
                        />

                        Loading
                      </>
                    ) : (
                      "Load"
                    )}

                  </Button>

                  <Button
                    className="report-btn"
                    variant="outline-secondary"
                    onClick={
                      handleClear
                    }
                    disabled={
                      loading ||
                      excelLoading ||
                      pdfLoading
                    }
                  >

                    <RefreshCcw
                      size={16}
                      className="me-2"
                    />

                    Clear

                  </Button>

                </div>

              </Col>

            </Row>

          </div>

          {/* ================================================= */}
          {/* TABLE TOOLBAR */}
          {/* ================================================= */}

          <div className="report-table-toolbar">

            <div className="report-table-title">

              Water DCB{" "}

              <span className="report-count">

                (
                FY:{" "}
                {fyYear || "-"}

                {" "}

                • Houses in current result:{" "}

                {meta?.returned_houses ?? 0}

                )

              </span>

              {meta?.start_date &&
              meta?.end_date ? (

                <div
                  className="text-muted"
                  style={{
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >

                  Range:{" "}
                  {meta.start_date}
                  {" "}
                  →
                  {" "}
                  {meta.end_date}

                </div>

              ) : null}

              {!home_id ? (

                <div
                  className="text-muted"
                  style={{
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >

                  Total Houses:{" "}
                  {totalRecords}

                </div>

              ) : null}

            </div>

            {/* =============================================== */}
            {/* SEARCH + EXPORT */}
            {/* =============================================== */}

            <div className="report-table-right">

              <InputGroup className="report-search">

                <InputGroup.Text>

                  <Search
                    size={16}
                  />

                </InputGroup.Text>

                <Form.Control
                  value={
                    search
                  }
                  onChange={(
                    event,
                  ) =>
                    setSearch(
                      event
                        .target
                        .value,
                    )
                  }
                  placeholder="Search within current page rows..."
                />

              </InputGroup>

              {/* ============================================= */}
              {/* EXCEL */}
              {/* ============================================= */}

              <Button
                className="report-btn"
                variant="outline-primary"
                onClick={
                  exportExcel
                }
                disabled={
                  !rows.length ||
                  loading ||
                  excelLoading ||
                  pdfLoading
                }
              >

                {excelLoading ? (

                  <Spinner
                    size="sm"
                    className="me-2"
                  />

                ) : (

                  <Download
                    size={16}
                    className="me-2"
                  />

                )}

                {excelLoading
                  ? "Exporting..."
                  : "Excel"}

              </Button>

              {/* ============================================= */}
              {/* PDF */}
              {/* ============================================= */}

              <Button
                className="report-btn"
                variant="outline-danger"
                onClick={
                  exportPDF
                }
                disabled={
                  !rows.length ||
                  loading ||
                  pdfLoading ||
                  excelLoading
                }
              >

                {pdfLoading ? (

                  <Spinner
                    size="sm"
                    className="me-2"
                  />

                ) : (

                  <Download
                    size={16}
                    className="me-2"
                  />

                )}

                {pdfLoading
                  ? "Exporting..."
                  : "PDF"}

              </Button>

            </div>

          </div>

          {/* ================================================= */}
          {/* TABLE */}
          {/* ================================================= */}

          <div className="report-table-wrap">

            <div className="report-table-scroll">

              <Table
                hover
                className="mb-0 report-table"
              >

                <thead>

                  <tr>

                    <SortableHeader field="home_id">
                      #
                    </SortableHeader>

                    <SortableHeader field="family_head">
                      Family Head
                    </SortableHeader>

                    <SortableHeader field="qr_code">
                      Meter
                    </SortableHeader>

                    <SortableHeader field="door_no">
                      Door
                    </SortableHeader>

                    <th>
                      Date
                    </th>

                    <th>
                      Opening
                    </th>

                    <th>
                      Bill No
                    </th>

                    <th>
                      Bill Amt
                    </th>

                    <th>
                      Receipt No
                    </th>

                    <th>
                      Mode
                    </th>

                    <th>
                      Receipt Amt
                    </th>

                    <th>
                      Discount Amt
                    </th>

                    <th>
                      Remarks
                    </th>

                    <th>
                      Closing
                    </th>

                  </tr>

                </thead>

                <tbody>

                  {loading ? (

                    <tr>

                      <td
                        colSpan={14}
                        className="text-center py-4"
                      >

                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />

                        Loading...

                      </td>

                    </tr>

                  ) : filteredRows.length ? (

                    filteredRows.map(
                      (
                        row,
                        index,
                      ) => {
                        if (
                          row.__blank
                        ) {
                          return (
                            <tr
                              key={
                                row.__key ||
                                `blank-${index}`
                              }
                              className="dcb-blank-row"
                            >

                              <td
                                colSpan={14}
                              >
                                &nbsp;
                              </td>

                            </tr>
                          );
                        }

                        return (

                          <tr
                            key={`${row.home_id}-${row.s_no}-${index}`}
                          >

                            <td>
                              {row.s_no ===
                              1
                                ? row.house_serial
                                : ""}
                            </td>

                            <td>
                              {row.s_no ===
                              1
                                ? row.family_head
                                : ""}
                            </td>

                            <td>
                              {row.s_no ===
                              1
                                ? row.qr_code
                                : ""}
                            </td>

                            <td>
                              {row.s_no ===
                              1
                                ? row.door_no
                                : ""}
                            </td>

                            <td>
                              {row.date}
                            </td>

                            <td>
                              {row.opening_balance}
                            </td>

                            <td>
                              {row.bill_no}
                            </td>

                            <td>
                              {row.bill_amount}
                            </td>

                            <td>
                              {row.rct_no}
                            </td>

                            <td>
                              {row.mode}
                            </td>

                            <td>
                              {row.rct_amount}
                            </td>

                            <td>
                              {row.discount_amount}
                            </td>

                            <td>
                              {row.discount_remarks}
                            </td>

                            <td>
                              {row.closing_balance}
                            </td>

                          </tr>

                        );
                      },
                    )

                  ) : (

                    <tr>

                      <td
                        colSpan={14}
                        className="text-center py-4"
                      >
                        No data found
                      </td>

                    </tr>

                  )}

                </tbody>

              </Table>

            </div>

            {/* ================================================= */}
            {/* PAGINATION */}
            {/* ================================================= */}

            <div className="report-pagination-bar">

              <div className="report-page-size">

                <span>
                  Rows:
                </span>

                <Form.Select
                  value={
                    pageSize
                  }
                  onChange={(
                    event,
                  ) => {
                    setPage(1);

                    setPageSize(
                      Number(
                        event
                          .target
                          .value,
                      ),
                    );
                  }}
                  className="page-size-select"
                  disabled={
                    loading ||
                    excelLoading ||
                    pdfLoading
                  }
                >

                  {[
                    5,
                    10,
                    20,
                    50,
                    100,
                  ].map(
                    (
                      size,
                    ) => (

                      <option
                        key={size}
                        value={size}
                      >
                        {size}
                      </option>

                    ),
                  )}

                </Form.Select>

              </div>

              <div className="report-pagination">

                {renderPagination()}

              </div>

              <div className="report-page-info">

                Page{" "}
                {currentPage}
                {" "}
                /
                {" "}
                {totalPages}

              </div>

            </div>

          </div>

        </Card.Body>

      </Card>

    </div>
  );
};

export default WaterDcbReportPage;